import React, { useMemo, useRef } from "react";
import makeStyles from "@mui/styles/makeStyles";
import { useTranslation } from "react-i18next";
import {
  calculateCropRect,
  cropRectToImagePosition,
  getBoxSize,
} from "../../utils";

import { useTheme } from "@mui/material/styles";
import { alpha } from "@mui/material/styles";
import Icon from "@mui/material/Icon";

import EpmsImage from "../Common/EpmsImage";
import { imageQualityWarning } from "../../lib/imageutils";
import { getImageResourceSize } from "../../Providers/TemplateInstanceProvider";
import { getRenderOrientation } from "@/react/components/Template/common";

const useStyles = makeStyles((theme) => ({
  outerContainer: {
    position: "absolute",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    overflow: "hidden",
    backgroundPosition: "center center",
    backgroundSize: "cover",
    backgroundRepeat: "no-repeat",
    "&.loading > div": {
      animation: "image-pulse 2s infinite",
      filter: "blur(4px)",
    },
  },
  renderPart: {
    position: "absolute",
    boxSizing: "border-box",
    overflow: "hidden",
    flexShrink: 0,
  },
  image: {
    position: "absolute",
    left: 0,
    top: 0,
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  selectText: {
    position: "absolute",
    display: "flex",
    width: "100%",
    height: "100%",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "10px",
    color: theme.palette.getContrastText(theme.palette.primary.main),
    textAlign: "center",
  },
  selectImage: {
    position: "absolute",
    display: "flex",
    width: "100%",
    height: "100%",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "16px",
    color: theme.palette.getContrastText(theme.palette.primary.main),
    backgroundColor: alpha(theme.palette.primary.main, 0.27),
    fontWeight: "bold",
  },
  "@global": {
    "@keyframes image-pulse": {
      "0%": {
        filter: "blur(4px)",
      },
      "70%": {
        filter: "blur(2px)",
      },
      "100%": {
        filter: "blur(4px)",
      },
    },
  },
  qualityWarning: {
    position: "absolute",
    right: "5px",
    bottom: "5px",
    color: "yellow",
    padding: 0,
  },
}));

const RenderImage = (props) => {
  if (props.resource.userSupplied) {
    return <RenderUserImage {...props} />;
  } else {
    return <RenderTemplateImage {...props} />;
  }
};

const RenderTemplateImage = (props) => {
  const classes = useStyles();
  const { renderPart, resource, scaleRatio } = props;

  const scaledRenderPart = useMemo(() => {
    return {
      width: renderPart.size.width * scaleRatio,
      height: renderPart.size.height * scaleRatio,
      x: renderPart.position.x * scaleRatio,
      y: renderPart.position.y * scaleRatio,
    };
  }, [renderPart, scaleRatio]);

  const styles = {
    width: scaledRenderPart.width + "px",
    height: scaledRenderPart.height + "px",
    top: scaledRenderPart.y + "px",
    left: scaledRenderPart.x + "px",
  };

  const url = resource.image?.url;

  const imageStyle = {
    objectFit: renderPart.image.adaption == "fill-in" ? "cover" : "contain",
  };

  return (
    <div className={classes.renderPart} style={styles}>
      {url && (
        <img
          src={url}
          className={classes.image}
          style={imageStyle}
          aria-hidden="true"
        />
      )}
    </div>
  );
};

export default RenderImage;

const RenderUserImage = ({
  renderPart,
  resource,
  handleUserImageClick,
  zoomResource,
  scaleRatio,
  mode,
  resourceNumber,
  zoomResourceId,
  templateInstance,
  userImageUrlCreator,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const { t } = useTranslation();
  const [loading, setLoading] = React.useState(true);

  const scaledRenderPart = useMemo(() => {
    return {
      width: renderPart.size.width * scaleRatio,
      height: renderPart.size.height * scaleRatio,
      x: renderPart.position.x * scaleRatio,
      y: renderPart.position.y * scaleRatio,
    };
  }, [renderPart, scaleRatio]);

  const handleOnClick = () => {
    handleOnLoad;
  };

  const cropRect = useMemo(() => {
    console.log("Crop rect resource", resource.image);

    if (!resource?.image.uuid) {
      return null;
    }

    // If we have a picture position, we don't need to calc (Used when dragging)
    if (resource.image.picturePosition) {
      return null;
    }

    if (resource.image.cropRect) {
      console.log("Got crop rect", resource.image.cropRect);
      const { x, y, width, height } = resource.image.cropRect;
      return {
        x: x,
        y: y,
        width,
        height,
      };
    }

    const imageSize = getImageResourceSize(resource.image, renderPart);

    const isRotated = renderPart.rotation == 90 || renderPart.rotation == 270;
    let sr = {
      width: isRotated ? scaledRenderPart.height : scaledRenderPart.width,
      height: isRotated ? scaledRenderPart.width : scaledRenderPart.height,
    };

    const pos = getBoxSize(renderPart.image.adaption, imageSize, sr);

    // Get crop rect for fill in:
    return calculateCropRect(pos, imageSize, sr);
    // Include render part as it might be scaled:
  }, [resource, renderPart, scaleRatio]);

  const picturePosition = useMemo(() => {
    if (!resource.image.uuid) {
      return null;
    }

    if (resource.image.picturePosition) {
      console.log(
        "Using saved picture position",
        resource.image.picturePosition,
      );
      return resource.image.picturePosition;
    }

    const imageSize = getImageResourceSize(resource.image, renderPart);

    let sr = { ...scaledRenderPart };

    if (renderPart.rotation == 270 || renderPart.rotation == 90) {
      sr.left = scaledRenderPart.top;
      sr.top = scaledRenderPart.left;
      sr.width = scaledRenderPart.height;
      sr.height = scaledRenderPart.width;
    }

    console.log(
      "WSL: Getting picture position",
      renderPart.rotation,
      cropRect,
      sr,
    );
    return cropRectToImagePosition(
      cropRect,
      imageSize,
      sr,
      renderPart.image.adaption,
    );
    // The renderpart might be scaled, so we also depend on that:
  }, [cropRect, resource, renderPart, scaleRatio]);

  const qualityWarning = useMemo(() => {
    // During update, no warning
    if (resource?.image.picturePosition || !resource?.image.uuid) {
      return false;
    }

    if (resource?.disableEdit) {
      return false;
    }

    const imagePixels = cropRect.width * cropRect.height;
    let imageArea = {
      width: renderPart.size.width,
      height: renderPart.size.height,
    };

    /* Actual pixels filled isn't really important?
    if (renderPart.image.adaption == "fit-in") {
        imageArea = {
            width: crop.rect.width < renderPart.size.width ? crop.rect.width :  renderPart.size.width,
            height: crop.rect.height < renderPart.size.height ? crop.rect.height :  renderPart.size.height,
        }
    }
     */
    let imageAreaPixels = imageArea.width * imageArea.height;

    if (templateInstance.template.categories.indexOf("STUDENT") >= 0) {
      // Super-hack Exakta/Student: Ingen krav til student-bilder
      if (
        ["order.exaktaphoto.se", "www.collagefoto.se"].indexOf(
          window.location.hostname,
        ) >= 0
      ) {
        return false;
      }
      if (imageAreaPixels > 3312401) {
        // Fallback hack? Require less pixels for student, maybe?
        imageAreaPixels = 3312401;
      }
    }

    return imageQualityWarning(imageAreaPixels, imagePixels);
  }, [cropRect, resource?.image.picturePosition]);

  const outerContainerStyles = {
    width: scaledRenderPart.width + "px",
    height: scaledRenderPart.height + "px",
    top: scaledRenderPart.y + "px",
    left: scaledRenderPart.x + "px",
  };

  const containerStyles = {};

  let imageStyle = {
    flexShrink: 0,
    // This is needed to get the proper placement. Don't remember why :/
    transform: "translateZ(0)",
    position: "absolute",
  };

  if (renderPart.rotation) {
    containerStyles.transform = "rotate(" + renderPart.rotation + "deg)";
    outerContainerStyles.justifyContent = "center";
    outerContainerStyles.alignItems = "center";
    outerContainerStyles.display = "flex";
  } else {
  }

  if (picturePosition) {
    imageStyle = {
      ...imageStyle,
      ...{
        width: parseInt(picturePosition.width) + "px",
        height: parseInt(picturePosition.height) + "px",
        top: parseInt(picturePosition.y) + "px",
        left: parseInt(picturePosition.x) + "px",
      },
    };
    containerStyles.width = picturePosition.container.width;
    containerStyles.height = picturePosition.container.height;
  }

  console.log("Dragging", zoomResourceId, resource.fullId);
  let selectText = "";
  if (
    (handleUserImageClick ||
      (zoomResource && zoomResource.fullId == resource.fullId)) &&
    !resource.image.uuid
  ) {
    outerContainerStyles.backgroundColor = theme.palette.primary.main;
    if (mode != "select-user-image") {
      selectText = t("Select image");
    }
  } else if (
    zoomResourceId &&
    zoomResourceId != resource.fullId &&
    !resource.disableEdit
  ) {
    console.log("Resource is not selected", resource.fullId, zoomResourceId);
    outerContainerStyles.backgroundColor = "#999";
  } else {
    console.log("RenderUserImage", zoomResource, resource);
  }

  const effect =
    resource.image.overrideEffect || renderPart.image.effect || "original";
  const rotation = resource.image.rotation % 360;

  const version = useMemo(
    () => `${resource.image.uuid}-${rotation}-${effect}`,
    [resource.image, rotation, effect],
  );
  const latestVersionRef = useRef(version);

  const handleOnLoad = () => {
    if (version === latestVersionRef.current) {
      console.log("✅ Image is current and safe to use:", version);
    } else {
      console.log("⚠️ Outdated image load ignored:", version);
    }
  };

  latestVersionRef.current = version;

  console.log(
    "Rotation",
    resource.image,
    renderPart.rotation,
    resource.image.rotation,
    resource.image.rotation + (renderPart.rotation % 360),
  );

  if (resource.image.background) {
    let orientation = "portrait";
    let bgSrc =
      "/system/gsbackground.php?uuid=" +
      resource.image.background.uuid +
      "&effect=" +
      effect;
    if (templateInstance.clientCompatibilityDate >= "2026-01-06") {
      console.log("Follow background! yey!");
      const backgroundRotation =
        (renderPart.rotation + resource.image.rotation) % 360;
      bgSrc += "&rotation=" + backgroundRotation;
      orientation = getRenderOrientation(renderPart, resource.image.rotation);
    } else {
      bgSrc += "&rotation=0";
    }
    bgSrc += "&orientation=" + orientation;

    outerContainerStyles.backgroundImage = `url('${bgSrc}')`;
    console.log("The styles is", outerContainerStyles);
  }

  console.log("Render image resource", resource, qualityWarning);

  return (
    <div
      style={outerContainerStyles}
      className={`outer-container ${classes.outerContainer} ${
        resource.image.loading ? "loading" : ""
      } `}
    >
      <div style={containerStyles} className={`${classes.renderPart}`}>
        {resource.image.uuid && (
          <div style={imageStyle}>
            <EpmsImage
              style={{ width: "100%", height: "100%" }}
              uuid={resource.image.uuid}
              effect={effect}
              rotation={rotation}
              size={640}
              urlCreator={userImageUrlCreator}
              aria-hidden="true"
              onLoad={handleOnLoad}
            />
          </div>
        )}
        {selectText && <div className={classes.selectText}>{selectText}</div>}
        {mode == "select-user-image" && (
          <div className={classes.selectImage}>{resourceNumber}</div>
        )}
        {qualityWarning && (
          <Icon className={classes.qualityWarning}>warning</Icon>
        )}
      </div>
    </div>
  );
};
